import { useState, useCallback } from 'react';
import getContactApi from 'api/contacts/getContactApi';

interface ContactDetailState {
  open: boolean;
  selectedContactId: string | number | null;
  contactData: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
}

export default function useContactDetail() {
  const [state, setState] = useState<ContactDetailState>({
    open: false,
    selectedContactId: null,
    contactData: null,
    loading: false,
    error: null,
  });

  const fetchContact = useCallback(async (id: string | number) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await getContactApi(id);
      setState((prev) => ({ ...prev, contactData: data, loading: false }));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load contact';
      setState((prev) => ({ ...prev, error: message, loading: false }));
    }
  }, []);

  const openPanel = useCallback(
    (id: string | number) => {
      setState((prev) => ({
        ...prev,
        open: true,
        selectedContactId: id,
        contactData: null,
        error: null,
      }));
      fetchContact(id);
    },
    [fetchContact],
  );

  const closePanel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      open: false,
      selectedContactId: null,
      contactData: null,
      error: null,
    }));
  }, []);

  const refetch = useCallback(() => {
    if (state.selectedContactId !== null) {
      fetchContact(state.selectedContactId);
    }
  }, [state.selectedContactId, fetchContact]);

  return {
    state,
    handler: { openPanel, closePanel, refetch },
  };
}
